import { ClickAwayListener, FormControl, makeStyles } from '@material-ui/core'
import React, { useEffect, useRef, useState } from 'react'
import type { Moment } from 'moment'
import moment from 'moment'
import type { PopperProps as MuiPopperProps } from '@material-ui/core/Popper'
import AddIcon from '@material-ui/icons/Add'
import IconButton from '../IconButton'
import ClearIcon from '@material-ui/icons/Clear'
import classnames from 'classnames'
import Paper from '../Paper'
import ResponsivePopper from '../ResponsivePopper'
import { useDatePickerStyles } from './useDatePickerStyles'
import Button from '../Button'
import type { BreakpointValues } from '@material-ui/core/styles/createBreakpoints'
import { useBreakpoint } from '../../hooks'
import { DialogActions, DialogTitle } from '../Dialog'
import InputLabel from '../InputLabel'
import type { DropdownSelectOption } from '../DropdownSelect'
import DropdownSelect from '../DropdownSelect'
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle'
import Typography from '../Typography'
import Grid from '../Grid'
import DateRangePickerController from './DateRangePickerController'
import Collapse from '../Collapse'

const useStyles = makeStyles((theme) => ({
  dialogRoot: {
    margin: theme.spacing(4),
    maxWidth: '450px',
    minWidth: '450px',
    border: 'none',
    [theme.breakpoints.down('xs')]: {
      minWidth: '100%',
      maxWidth: '100%'
    }
  },
  dialogTitle: {
    [theme.breakpoints.down('sm')]: {
      paddingTop: 0,
      border: 'none'
    }
  },
  addButton: {
    marginBottom: theme.spacing(2.75),
    [theme.breakpoints.down('lg')]: {
      marginRight: theme.spacing(-0.2)
    },
    [theme.breakpoints.down('md')]: {
      marginRight: theme.spacing(-0.4)
    },
    [theme.breakpoints.down('sm')]: {
      padding: 0
    },
    [theme.breakpoints.down('xs')]: {
      marginRight: theme.spacing(0.4)
    }
  },
  clearIcon: {
    padding: 0,
    marginBottom: '4px'
  },
  availability: {
    marginTop: theme.spacing(-2),
    padding: theme.spacing(0, 4, 3, 4),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(0)
    }
  },
  availabilityTitle: {
    marginBottom: theme.spacing(1)
  },
  overrideRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5)
  },
  dropdown: {
    height: theme.typography.pxToRem(36)
  },
  unavailableText: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  removeButton: {
    padding: theme.spacing(0),
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    '&:hover': {
      color: theme.palette.error.main
    }
  },
  dialogActions: {
    border: 'none',
    [theme.breakpoints.down('sm')]: {
      padding: 0
    }
  }
}))

export interface DateOverridePickerProps {
  disabled?: boolean
  PopperProps?: Partial<MuiPopperProps>
  modalBreakpoint?: keyof BreakpointValues
  trigger?: React.ReactNode
  onPopperOpen?: () => void
  onPopperClose?: () => void
  onConfirm: (overrides: { start: Moment; end: Moment }[]) => void
  initialDays?: string[]
  initialTimes?: { startTime: number; endTime: number }[]
}

const timeOptions: DropdownSelectOption[] = Array.from({ length: 25 }, (_, i) => {
  const time = moment()
    .startOf('day')
    .add(7 * 60 + i * 30, 'minutes') // Start at 7:00 AM and increment by 30 minutes
  return {
    value: time.format('hh:mma'),
    label: time.format('h:mma')
  }
})

export function DateOverridePicker(props: DateOverridePickerProps) {
  const {
    PopperProps,
    modalBreakpoint = 'sm',
    onConfirm,
    onPopperOpen,
    onPopperClose,
    trigger,
    initialDays,
    initialTimes
  } = props
  const mobile = useBreakpoint((breakpoints) => breakpoints.down(modalBreakpoint))

  const [tableHeight, setTableHeight] = useState(0)

  const anchorRef = useRef(null)
  const [anchorEl, setAnchorEl] = useState(null)

  const [dates, setDates] = useState<Moment[]>([])
  const [overrides, setOverrides] = useState<{ start: string; end: string }[]>([])
  const [initialMonth, setInitialMonth] = useState(() => moment())

  useEffect(() => {
    if (anchorEl) {
      // Only reset when popper opens
      if (initialDays && initialDays.length > 0) {
        setDates(initialDays.map((day) => moment(day)))
      } else {
        setDates([])
      }
      if (initialTimes && initialTimes.length > 0) {
        const isUnavailable = initialTimes.some((tg) => tg.startTime === tg.endTime)
        if (isUnavailable) {
          setOverrides([])
          return
        }
        setOverrides(
          initialTimes.map((tg) => ({
            start: moment(tg.startTime).format('hh:mma'),
            end: moment(tg.endTime).format('hh:mma')
          }))
        )
      } else {
        setOverrides([])
      }
    }
    // eslint-disable-next-line
  }, [anchorEl, initialDays, initialTimes])

  useEffect(() => {
    recalculateTableHeight()
  }, [anchorEl, dates?.[0]])

  const recalculateTableHeight = () => {
    const elements = document.getElementsByClassName('CalendarMonthGrid_month__horizontal')
    if (elements.length > 0) {
      const height = (elements[0] as any).offsetHeight
      setTableHeight(height)
    }
  }

  const datePickerClasses = useDatePickerStyles({
    isSingle: false,
    tableHeight,
    blockPastDays: true,
    disableTodayOutline: true
  })
  const classes = useStyles()

  const handleClickAway = (e: React.MouseEvent<Document, MouseEvent>) => {
    if (e.target instanceof HTMLElement && e.target.className.includes('top-navigation')) {
      // This means the click was in a nested dropdown, so we don't want to close the date picker
      return
    }
    if (anchorEl) {
      setAnchorEl(null)
      onPopperClose?.()
      setDates([])
      setOverrides([])
    }
  }

  const handleChange = (start: Moment, end: Moment) => {
    // Set the dates to all the days in the range
    const startDate = start.clone().startOf('day')
    const endDate = end ? end.clone().endOf('day') : startDate.clone()
    const newDates = []
    const currentDate = startDate.clone()
    while (currentDate.isSameOrBefore(endDate, 'day')) {
      newDates.push(currentDate.clone())
      currentDate.add(1, 'day')
    }

    setDates(newDates)
  }

  const handleConfirm = () => {
    let formattedOverrides
    if (overrides.length === 0) {
      // If no overrides are set, we need to mark each date as unavailable
      // We'll set the avaiability for that day to 12:00 am to 12:00 am, which effectively makes it unavailable
      formattedOverrides = dates.map((date) => ({
        start: date.startOf('day'),
        end: date.startOf('day')
      }))
    } else {
      // For each date, create an override for each time range
      formattedOverrides = dates.flatMap((date) =>
        overrides.map((override) => {
          const start = moment(`${date.format('YYYY-MM-DD')} ${override.start}`, 'YYYY-MM-DD hh:mma')
          const end = moment(`${date.format('YYYY-MM-DD')} ${override.end}`, 'YYYY-MM-DD hh:mma')
          return { start, end }
        })
      )
    }
    onConfirm(formattedOverrides)
    setAnchorEl(null)
    onPopperClose?.()
    setDates([])
    setOverrides([])
  }

  const handleClear = (e) => {
    e.stopPropagation()
    setDates([])
    setOverrides([])
    setAnchorEl(null)
    onPopperClose?.()
  }

  const handleTriggerClick = () => {
    if (anchorEl) {
      setAnchorEl(null)
      onPopperClose?.()
    } else {
      // Set the initial month based on the first date (if any), otherwise default to today
      if (initialDays && initialDays.length > 0) {
        setInitialMonth(moment(initialDays[0]))
      } else {
        setInitialMonth(moment())
      }
      onPopperOpen?.()
      setAnchorEl(anchorRef.current)
    }
  }

  const handleAddOverride = () => {
    let start = '07:00am'
    if (overrides.length > 0) {
      start = overrides[overrides.length - 1].end
    }
    setOverrides([
      ...overrides,
      {
        start,
        end: '07:00pm'
      }
    ])
  }

  const areOverridesFullDay = (overrides: { start: string; end: string }[]) => {
    if (!overrides.length) {
      return false
    }
    const sorted = [...overrides].sort((a, b) => moment(a.start, 'hh:mma').diff(moment(b.start, 'hh:mma')))
    return sorted.length > 0 && sorted[0].start === '07:00am' && sorted[sorted.length - 1].end === '07:00pm'
  }

  const getOverlappingOverrideIndices = (overrides: { start: string; end: string }[]) => {
    const sorted = [...overrides].sort((a, b) => moment(a.start, 'hh:mma').diff(moment(b.start, 'hh:mma')))
    const overlapping: number[] = []
    for (let i = 1; i < sorted.length; i++) {
      const prevEnd = moment(sorted[i - 1].end, 'hh:mma')
      const currStart = moment(sorted[i].start, 'hh:mma')
      if (currStart.isBefore(prevEnd)) {
        overlapping.push(i - 1, i)
      }
    }
    // Remove duplicates and sort
    return Array.from(new Set(overlapping)).sort((a, b) => a - b)
  }

  const overlappingTimestamps = getOverlappingOverrideIndices(overrides)

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <FormControl fullWidth>
        {React.isValidElement(trigger) ? (
          React.cloneElement(trigger as React.ReactElement<any>, {
            ...trigger.props,
            onClick: handleTriggerClick,
            ref: anchorRef
          })
        ) : (
          <Button
            size={mobile ? 'medium' : 'small'}
            variant="hoverUnderline"
            color="primary"
            startIcon={<AddIcon />}
            className={classes.addButton}
            onClick={handleTriggerClick}
            ref={anchorRef}
          >
            New
          </Button>
        )}

        <ResponsivePopper
          classes={{
            root: datePickerClasses.popper,
            dialogRoot: classnames(datePickerClasses.popper, classes.dialogRoot),
            dialogContent: datePickerClasses.dialogContent
          }}
          open={!!anchorEl}
          PopperProps={{
            anchorEl,
            placement: 'bottom-end',
            modifiers: {
              flip: {
                enabled: false
              }
            },
            ...PopperProps
          }}
          modalBreakpoint={modalBreakpoint}
          footer={
            <DialogActions classes={{ root: classes.dialogActions }}>
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={dates.length === 0 || overlappingTimestamps.length > 0}
              >
                Confirm
              </Button>
            </DialogActions>
          }
        >
          <Paper style={{ boxShadow: 'none', border: 'none' }}>
            <DialogTitle
              classes={{ root: classes.dialogTitle }}
              rightContent={
                <IconButton size="small" className={classes.clearIcon} onClick={handleClear}>
                  <ClearIcon />
                </IconButton>
              }
            >
              {trigger ? '' : 'Add Date Override'}
            </DialogTitle>
            <Grid container justifyContent="center">
              <Grid item>
                <DateRangePickerController
                  startDate={dates[0]}
                  endDate={dates[dates.length - 1] || null}
                  onChange={handleChange}
                  recalculateTableHeight={recalculateTableHeight}
                  blockPastDays
                  initialVisibleMonth={() => initialMonth}
                />
              </Grid>

              <Grid item xs={12} className={classes.availability}>
                <Collapse show={dates.length > 0}>
                  <InputLabel className={classes.availabilityTitle}>Hours you're available:</InputLabel>
                  {!overrides.length && (
                    <Typography weight={500} color="textSecondary" className={classes.unavailableText}>
                      <i>Unavailable</i>
                    </Typography>
                  )}
                  {[...overrides]
                    .sort((a, b) => moment(a.end, 'hh:mma').diff(moment(b.end, 'hh:mma')))
                    .map((override, index) => {
                      // Find the index of the current start and end in timeOptions
                      const startIdx = timeOptions.findIndex((option) => option.value === override.start)
                      const endIdx = timeOptions.findIndex((option) => option.value === override.end)

                      // Filter options for start and end dropdowns
                      const startOptions = timeOptions.slice(0, endIdx > 0 ? endIdx : undefined)
                      const endOptions = timeOptions.slice(startIdx + 1)

                      return (
                        <div key={index} className={classes.overrideRow}>
                          <DropdownSelect
                            className={classes.dropdown}
                            hideIcon
                            variant="single"
                            value={timeOptions.find((option) => option.value === override.start)}
                            options={startOptions}
                            onChange={(option: DropdownSelectOption) => {
                              const newOverrides = [...overrides]
                              newOverrides[index] = {
                                ...newOverrides[index],
                                start: option.value
                              }
                              setOverrides(newOverrides)
                            }}
                            showErrorBeforeValidation
                            error={overlappingTimestamps.includes(index)}
                          />
                          <Typography>—</Typography>
                          <DropdownSelect
                            className={classes.dropdown}
                            hideIcon
                            variant="single"
                            value={timeOptions.find((option) => option.value === override.end)}
                            options={endOptions}
                            onChange={(option: DropdownSelectOption) => {
                              const newOverrides = [...overrides]
                              newOverrides[index] = {
                                ...newOverrides[index],
                                end: option.value
                              }
                              setOverrides(newOverrides)
                            }}
                            showErrorBeforeValidation
                            error={overlappingTimestamps.includes(index)}
                          />
                          <IconButton
                            size="small"
                            className={classes.removeButton}
                            onClick={(e) => {
                              e.stopPropagation()
                              const newOverrides = [...overrides]
                              newOverrides.splice(index, 1)
                              setOverrides(newOverrides)
                            }}
                          >
                            <RemoveCircleIcon fontSize="small" />
                          </IconButton>
                        </div>
                      )
                    })}
                  <Collapse show={overlappingTimestamps.length > 0}>
                    <Typography color="error" style={{ marginBottom: 8 }}>
                      Please adjust your available hours so that none of them overlap.
                    </Typography>
                  </Collapse>
                  <Button
                    color={areOverridesFullDay(overrides) ? 'default' : 'primary'}
                    startIcon={<AddIcon />}
                    variant="hoverUnderline"
                    onClick={handleAddOverride}
                    disabled={areOverridesFullDay(overrides)}
                    style={{ padding: 0, paddingBottom: 4 }}
                  >
                    Add
                  </Button>
                </Collapse>
              </Grid>
            </Grid>
          </Paper>
        </ResponsivePopper>
      </FormControl>
    </ClickAwayListener>
  )
}
